import React, { useMemo } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Cpu,
  Search,
  X,
  ZapOff,
} from 'lucide-react';
import { useLogicStore, visibleLogicIssues, logicHealthOk } from '../../../store/logicStore';
import { useProjectStore } from '../../../store/projectStore';
import type { BackupPowerIsland, DeviceLogicState, LogicIssue, LogicIssueCategory } from '../../../types/deviceModel';
import { setNodesLogicExcluded } from '../../../utils/logicParticipation';
import UsageBar, { fmtWatts, fmtHours } from './UsageBar';

const FILTERS: Array<{ id: LogicIssueCategory | 'all'; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'power', label: 'Power' },
  { id: 'poe', label: 'PoE' },
  { id: 'physical', label: 'Physical' },
  { id: 'ports', label: 'Ports' },
];

const SOURCE_KINDS = new Set(['ac', 'dc-jack', 'dc-terminal', 'assigned-psu', 'cabled-psu']);

function focusIssue(issue: LogicIssue) {
  const store = useProjectStore.getState();
  if (issue.equipmentId) {
    useLogicStore.getState().setFocusDeviceId(issue.equipmentId);
  }
  if (issue.cableId) {
    const design = store.getCurrentDesign();
    const cable = design?.cables.find((c) => c.id === issue.cableId);
    if (cable) {
      store.setSelectedEquipment(null);
      store.setSelectedRack(null);
      store.setSelectedCable(cable);
      store.setCableConfigOpen(false);
      store.setCablePortEditorOpen(true);
      store.setEquipmentConfigOpen(false);
    }
    return;
  }
  if (issue.equipmentId) {
    const design = store.getCurrentDesign();
    const eq = design?.equipment.find((e) => e.id === issue.equipmentId);
    if (eq) {
      store.setSelectedEquipment(eq);
      store.setSelectedEquipmentForPortView(eq);
      store.setPortVisualizationOpen(true);
      store.setEquipmentConfigOpen(false);
      store.setSelectedCable(null);
    }
  }
}

/** Same canvas pink outline as badge hover. */
function highlightCanvasEquipment(nodeId: string | null | undefined) {
  useProjectStore.getState().setHoveredEquipmentId(nodeId || null);
}

function BackupIslandView({
  island,
  isDark,
  cardMuted,
  labelTrackers,
}: {
  island: BackupPowerIsland;
  isDark: boolean;
  cardMuted: string;
  labelTrackers?: boolean;
}) {
  return (
    <div className="space-y-3">
      <div className="text-sm" style={{ color: cardMuted }}>
        Battery {island.batteryVolts > 0 ? `${Math.round(island.batteryVolts)} V` : '—'}
        {island.batteryAh > 0 ? ` · ${Math.round(island.batteryAh)} Ah` : ''}
        {island.usableWh > 0 ? ` · ${Math.round(island.usableWh)} Wh usable` : ''}
      </div>
      <div className="text-sm" style={{ color: cardMuted }}>
        Solar charge {fmtWatts(island.solarStcWatts)} STC (not in overnight hours)
      </div>
      <UsageBar
        title="PV"
        used={island.pvUsedWatts}
        total={island.pvMaxWatts}
        usedLabel="Array"
        restLabel="Headroom"
        accent="#fbbf24"
        isDark={isDark}
        emptyText="No PV/MPPT rating"
        totalSuffix="Max"
      />
      {island.pvTrackers.length > 0 && (
        <div className="space-y-0.5 text-sm" style={{ color: cardMuted }}>
          {island.pvTrackers.map((t) => (
            <div key={t.id}>
              {labelTrackers || island.pvTrackers.length > 1 ? `${t.label}: ` : ''}
              {t.arrayVolts > 0 ? `Array ${Math.round(t.arrayVolts)} V` : 'Array —'}
              {t.mpptMinVolts != null ? ` · Min ${Math.round(t.mpptMinVolts)} V to use` : ''}
            </div>
          ))}
        </div>
      )}
      <UsageBar
        title="Inverter AC Output"
        used={island.loadWatts}
        total={island.inverterAcWatts}
        usedLabel="Load"
        restLabel="Headroom"
        accent="#34d399"
        isDark={isDark}
        emptyText="No AC output rating"
        totalSuffix="Max"
      />
      <div className="text-sm" style={{ color: cardMuted }}>
        No PV Backup Time: {fmtHours(island.overnightHours)}
        {island.loadWatts <= 0 ? ' (no in-Logic load)' : ` at ${fmtWatts(island.loadWatts)}`}
        {` · ${Math.round(island.efficiency * 100)}% eff`}
      </div>
    </div>
  );
}

function aggregateBackupIslands(islands: BackupPowerIsland[]): BackupPowerIsland | null {
  if (islands.length === 0) return null;
  if (islands.length === 1) return islands[0]!;
  const volts = islands.map((i) => i.batteryVolts).filter((v) => v > 0);
  const sameVolts = volts.length <= 1 || Math.max(...volts) - Math.min(...volts) < 1;
  return {
    inverterId: '',
    inverterName: 'Whole design',
    batteryVolts: sameVolts ? volts[0] || 0 : 0,
    batteryAh: islands.reduce((s, i) => s + (i.batteryAh || 0), 0),
    usableWh: islands.reduce((s, i) => s + (i.usableWh || 0), 0),
    solarStcWatts: islands.reduce((s, i) => s + (i.solarStcWatts || 0), 0),
    pvUsedWatts: islands.reduce((s, i) => s + (i.pvUsedWatts || 0), 0),
    pvMaxWatts: islands.reduce((s, i) => s + (i.pvMaxWatts || 0), 0),
    pvTrackers: islands.flatMap((i) =>
      i.pvTrackers.map((t) => ({
        ...t,
        id: `${i.inverterId}:${t.id}`,
        label: `${i.inverterName} · ${t.label}`,
      }))
    ),
    // Load is design-wide (same on each island); AC max is summed across inverters.
    loadWatts: islands[0]?.loadWatts || 0,
    inverterAcWatts: islands.reduce((s, i) => s + (i.inverterAcWatts || 0), 0),
    efficiency: islands[0]?.efficiency || 0.97,
    overnightHours: islands[0]?.overnightHours ?? null,
  };
}

function isBackupEnergyDevice(d: DeviceLogicState): boolean {
  return d.type === 'solar-panel' || d.type === 'battery' || d.type === 'inverter';
}

function designPowerStats(
  devices: DeviceLogicState[],
  backup?: { islands: Array<{ inverterId: string; inverterAcWatts: number; loadWatts: number }> } | null
) {
  const inLogic = devices.filter((d) => !d.excluded);
  const powered = inLogic.filter((d) => d.powered);
  const byId = new Map(devices.map((d) => [d.nodeId, d]));

  // Consumption: non-PoE-in loads (PoE PDs are counted via upstream poeUsedWatts).
  const used = powered.reduce((s, d) => {
    if (d.type === 'solar-panel' || d.type === 'battery' || d.type === 'wall-outlet') return s;
    if (d.winningInputKind === 'poe-in') return s;
    return s + (d.selfDrawWatts || 0) + (d.poeUsedWatts || 0);
  }, 0);

  const liveInverters = (backup?.islands || []).filter((island) => {
    const d = byId.get(island.inverterId);
    return d && !d.excluded && d.powered;
  });

  let total = 0;
  if (liveInverters.length > 0) {
    // Shared backup AC bus — capacity is inverter AC output, not every inlet summed.
    total = liveInverters.reduce((s, i) => s + (i.inverterAcWatts || 0), 0);
  } else {
    const sources = powered.filter(
      (d) =>
        !isBackupEnergyDevice(d) &&
        d.winningInputKind &&
        SOURCE_KINDS.has(d.winningInputKind) &&
        (d.inputWatts || 0) > 0
    );
    const acSources = sources.filter((d) => d.winningInputKind === 'ac');
    const dedicated = sources.filter((d) => d.winningInputKind !== 'ac');
    // Assumed wall AC is one shared pool — take the largest inlet, don't sum every device.
    const acPool = acSources.length > 0 ? Math.max(...acSources.map((d) => d.inputWatts || 0)) : 0;
    const dedicatedPool = dedicated.reduce((s, d) => s + (d.inputWatts || 0), 0);
    total = acPool + dedicatedPool;
  }

  const poeBudget = powered.reduce((s, d) => s + (d.poeBudgetWatts || 0), 0);
  const poeUsed = powered.reduce((s, d) => s + (d.poeUsedWatts || 0), 0);
  return { total, used, poeBudget, poeUsed };
}

function devicePowerStats(d: DeviceLogicState) {
  const self = d.selfDrawWatts || 0;
  const rated = d.ratedSelfDrawWatts ?? self;
  const poe = d.poeUsedWatts || 0;
  const input = d.inputWatts || 0;
  const used = d.powered ? self + poe : 0;
  const total = d.powered ? (input > 0 ? input : Math.max(self, used)) : 0;
  return {
    used,
    total,
    self,
    rated,
    poe,
    input,
    poeBudget: d.powered ? d.poeBudgetWatts || 0 : 0,
    poeUsed: d.powered ? poe : 0,
    isPse: !!(d.powered && d.isPse),
  };
}

function AccordionSection({
  title,
  badge,
  expanded,
  onToggle,
  children,
  muted,
  cardBg,
  cardText,
}: {
  title: React.ReactNode;
  badge?: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  muted: string;
  cardBg: string;
  cardText: string;
}) {
  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ backgroundColor: cardBg, color: cardText }}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3.5 py-2.5 text-sm font-semibold hover:bg-white/5"
        style={{ color: cardText }}
      >
        {expanded ? <ChevronDown size={16} style={{ color: muted }} /> : <ChevronRight size={16} style={{ color: muted }} />}
        <span className="flex-1 text-left min-w-0">{title}</span>
        {badge}
      </button>
      {expanded && <div className="px-3.5 pb-3.5">{children}</div>}
    </div>
  );
}

function DeviceList({
  devices,
  focusId,
  muted,
  onToggle,
  onSelect,
}: {
  devices: DeviceLogicState[];
  focusId: string | null;
  muted: string;
  onToggle: (id: string, nextIncluded: boolean) => void;
  onSelect: (id: string) => void;
}) {
  if (devices.length === 0) {
    return <p className="text-sm py-1" style={{ color: muted }}>No devices</p>;
  }
  return (
    <div className="space-y-0.5">
      {devices.map((d) => {
        const inLogic = !d.excluded;
        const focused = focusId === d.nodeId;
        return (
          <div
            key={d.nodeId}
            className={`flex items-center gap-2 rounded px-1.5 py-1.5 ${focused ? 'bg-white/10' : 'hover:bg-white/5'}`}
            onMouseEnter={() => highlightCanvasEquipment(d.nodeId)}
            onMouseLeave={() => highlightCanvasEquipment(null)}
          >
            <input
              type="checkbox"
              checked={inLogic}
              onChange={() => onToggle(d.nodeId, !inLogic)}
              aria-label={inLogic ? `Exclude ${d.name} from Logic` : `Include ${d.name} in Logic`}
              title={inLogic ? 'Exclude from Logic' : 'Include in Logic'}
            />
            <button
              type="button"
              className="flex-1 min-w-0 text-left text-sm truncate"
              onClick={() => onSelect(d.nodeId)}
            >
              {d.name}
            </button>
            {d.poweredOff ? (
              <span className="text-xs text-slate-400 shrink-0">Off</span>
            ) : inLogic && !d.powered ? (
              <span className="text-xs text-amber-400 shrink-0">Unpowered</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

const LogicStatusDrawer: React.FC<{ isDark: boolean }> = ({ isDark }) => {
  const design = useProjectStore((s) => s.getCurrentDesign());
  const mode = design?.logicMode === 'logic' ? 'logic' : 'plan';
  const {
    drawerOpen,
    setDrawerOpen,
    hideIdle,
    setHideIdle,
    issueFilter,
    setIssueFilter,
    search,
    setSearch,
    issueGroupBy,
    setIssueGroupBy,
    focusDeviceId,
    setFocusDeviceId,
    result,
  } = useLogicStore();
  const [openSections, setOpenSections] = React.useState({ power: true, backupPower: true, issues: true, devices: true });
  const [focusInverterId, setFocusInverterId] = React.useState('');
  const toggleSection = (key: keyof typeof openSections) =>
    setOpenSections((s) => ({ ...s, [key]: !s[key] }));

  const muted = isDark ? '#94a3b8' : '#475569';
  const bg = isDark ? '#20242b' : '#0f172a';
  const border = isDark ? '#343a43' : '#334155';
  const text = isDark ? '#f3f4f6' : '#e2e8f0';
  const cardBg = isDark ? '#3a414c' : '#1e293b';
  const cardText = text;
  const cardMuted = muted;

  const allIssues = useMemo(
    () => visibleLogicIssues(result, hideIdle, issueFilter, search),
    [result, hideIdle, issueFilter, search]
  );

  const tabCounts = useMemo(() => {
    const list = visibleLogicIssues(result, hideIdle, 'all', '');
    return {
      errors: list.filter((i) => i.severity === 'error').length,
      warnings: list.filter((i) => i.severity === 'warning').length,
    };
  }, [result, hideIdle]);

  const focused = focusDeviceId && result?.devices[focusDeviceId] ? result.devices[focusDeviceId] : null;

  const designIssues = allIssues;
  const errors = designIssues.filter((i) => i.severity === 'error');
  const warnings = designIssues.filter((i) => i.severity === 'warning');
  const infos = designIssues.filter((i) => i.severity === 'info');
  const ok = logicHealthOk(result, hideIdle);
  const overview = result?.overview;

  const grouped = useMemo(() => {
    if (issueGroupBy === 'issue') {
      const buckets: Array<[string, LogicIssue[]]> = [
        ['Errors', errors],
        ['Warnings', warnings],
        ['Info', infos],
      ];
      return buckets.filter(([, list]) => list.length > 0);
    }
    const map = new Map<string, LogicIssue[]>();
    designIssues.forEach((i) => {
      const key = i.equipmentId || i.cableId || 'other';
      const list = map.get(key) || [];
      list.push(i);
      map.set(key, list);
    });
    return Array.from(map.entries());
  }, [issueGroupBy, designIssues, errors, warnings, infos]);

  const allDevices = result
    ? Object.values(result.devices).sort((a, b) => a.name.localeCompare(b.name))
    : [];
  const includedCount = allDevices.filter((d) => !d.excluded).length;

  const power = useMemo(() => {
    if (!result) return null;
    if (focused) return { kind: 'device' as const, ...devicePowerStats(focused) };
    const devices = Object.values(result.devices);
    return { kind: 'design' as const, ...designPowerStats(devices, result.backupPower) };
  }, [result, focused]);

  const backupIslands = result?.backupPower?.islands || [];
  const focusInverterValid = focusInverterId && backupIslands.some((i) => i.inverterId === focusInverterId)
    ? focusInverterId
    : '';
  const backupView = useMemo(() => {
    if (backupIslands.length === 0) return null;
    if (focusInverterValid) {
      return backupIslands.find((i) => i.inverterId === focusInverterValid) || null;
    }
    const agg = aggregateBackupIslands(backupIslands);
    if (!agg) return null;
    return {
      ...agg,
      overnightHours: result?.backupPower?.overnightHours ?? agg.overnightHours,
      usableWh: result?.backupPower?.usableWh ?? agg.usableWh,
      solarStcWatts: result?.backupPower?.solarStcWatts ?? agg.solarStcWatts,
      loadWatts: result?.backupPower?.loadWatts ?? agg.loadWatts,
    };
  }, [backupIslands, focusInverterValid, result?.backupPower]);

  const selectDevice = (id: string) => {
    const next = id || null;
    setFocusDeviceId(next);
    if (!next) return;
    const eq = design?.equipment.find((e) => e.id === next);
    if (eq) {
      const store = useProjectStore.getState();
      store.setSelectedEquipment(eq);
      useLogicStore.getState().setSelectionIds([next]);
    }
  };

  if (mode !== 'logic') return null;

  if (!drawerOpen) {
    return (
      <div
        className="absolute top-14 right-0 z-40 pointer-events-auto flex flex-col items-center"
        data-logic-drawer-tab="true"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="flex flex-col items-center gap-1.5 px-1 py-2"
          title={`Open Logic status${tabCounts.errors || tabCounts.warnings ? ` · ${tabCounts.errors} error${tabCounts.errors === 1 ? '' : 's'} · ${tabCounts.warnings} warning${tabCounts.warnings === 1 ? '' : 's'}` : ''}`}
        >
          <span
            className="text-[10px] font-semibold tracking-wider uppercase"
            style={{ color: muted, writingMode: 'vertical-rl' }}
          >
            Logic
          </span>
          <span
            className="min-w-[18px] px-1 rounded-full text-[9px] font-bold leading-[16px] text-center"
            style={{
              backgroundColor: tabCounts.errors > 0 ? 'rgba(248,113,113,0.22)' : (isDark ? '#2b3140' : '#e5e7eb'),
              color: tabCounts.errors > 0 ? '#f87171' : muted,
            }}
          >
            {tabCounts.errors}
          </span>
          <span
            className="min-w-[18px] px-1 rounded-full text-[9px] font-bold leading-[16px] text-center"
            style={{
              backgroundColor: tabCounts.warnings > 0 ? 'rgba(251,191,36,0.22)' : (isDark ? '#2b3140' : '#e5e7eb'),
              color: tabCounts.warnings > 0 ? '#fbbf24' : muted,
            }}
          >
            {tabCounts.warnings}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div
      className="absolute top-0 right-0 bottom-0 z-50 flex flex-col pointer-events-auto overflow-hidden shadow-2xl border-l"
      style={{
        width: 384,
        backgroundColor: bg,
        borderColor: border,
        color: text,
      }}
      data-logic-drawer="true"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      <div className="flex-shrink-0 px-5 py-4" style={{ borderBottom: `1px solid ${border}` }}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold leading-tight" style={{ color: isDark ? '#e5e7eb' : '#93c5fd' }}>
              Logic Status
            </h2>
            {!ok && (
              <p className="text-sm mt-1" style={{ color: muted }}>
                {`${errors.length} error${errors.length === 1 ? '' : 's'} · ${warnings.length} warning${warnings.length === 1 ? '' : 's'}`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {ok ? (
              <CheckCircle2 size={20} className="text-emerald-400" />
            ) : (
              <AlertTriangle size={20} className="text-amber-400" />
            )}
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="p-1.5 rounded-md hover:bg-white/10"
              style={{ color: muted }}
              title="Close Logic status"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-3 py-3 space-y-3">
        <AccordionSection
          title={
            <span className="flex items-center gap-2 min-w-0 w-full">
              <span className="shrink-0">Power Stats for:</span>
              <select
                value={focusDeviceId && result?.devices[focusDeviceId] ? focusDeviceId : ''}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onChange={(e) => selectDevice(e.target.value)}
                className="flex-1 min-w-0 text-sm font-medium rounded px-2 py-1 outline-none"
                style={{ backgroundColor: isDark ? '#2b3140' : '#1e293b', color: cardText, border: `1px solid ${border}` }}
                title="Show power stats for one device or the whole design"
              >
                <option value="">Whole design</option>
                {allDevices.map((d) => (
                  <option key={d.nodeId} value={d.nodeId}>
                    {d.name}
                    {d.excluded ? ' (excluded)' : d.powered ? '' : ' (off)'}
                  </option>
                ))}
              </select>
            </span>
          }
          expanded={openSections.power}
          onToggle={() => toggleSection('power')}
          muted={cardMuted}
          cardBg={cardBg}
          cardText={cardText}
        >
          {power ? (
            <div className="space-y-4">
              {power.kind === 'device' ? (
                <>
                  <UsageBar
                    title="Power draw"
                    used={focused?.powered ? power.used : 0}
                    total={focused?.powered ? power.total : 0}
                    usedLabel="Used"
                    restLabel="Headroom"
                    accent="#60a5fa"
                    isDark={isDark}
                    emptyText={focused?.powered ? undefined : `Unpowered · needs ${fmtWatts(power.rated)}`}
                  />
                  {power.isPse && (
                    <UsageBar
                      title="PoE budget"
                      used={focused?.powered ? power.poeUsed : 0}
                      total={focused?.powered ? power.poeBudget : 0}
                      usedLabel="Used"
                      restLabel="Remaining"
                      accent="#22d3ee"
                      isDark={isDark}
                      emptyText="No PoE-out on this device"
                    />
                  )}
                  {focused && (
                    <div className="text-sm" style={{ color: cardMuted }}>
                      {focused.powered ? 'Powered' : 'Unpowered'}
                      {focused.winningInputKind ? ` · ${focused.winningInputKind}` : ''}
                      {focused.powered
                        ? power.isPse
                          ? ` · Self ${fmtWatts(power.self)} · PoE out ${fmtWatts(power.poe)}`
                          : ` · Draw ${fmtWatts(power.self)}`
                        : ` · Needs ${fmtWatts(power.rated)}`}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <UsageBar
                    title="Power draw"
                    used={power.used}
                    total={power.total}
                    usedLabel="Used"
                    restLabel="Headroom"
                    accent="#60a5fa"
                    isDark={isDark}
                    emptyText="No AC/DC sources in Logic"
                  />
                  <UsageBar
                    title="PoE budget"
                    used={power.poeUsed}
                    total={power.poeBudget}
                    usedLabel="Used"
                    restLabel="Remaining"
                    accent="#22d3ee"
                    isDark={isDark}
                    emptyText="No PoE sources in this design"
                  />
                  {overview && (
                    <div className="text-sm" style={{ color: cardMuted }}>
                      Powered {overview.powered}/{overview.devicesInLogic}
                      {overview.unpowered ? ` · Unpowered ${overview.unpowered}` : ''}
                      {overview.excluded ? ` · Excluded ${overview.excluded}` : ''}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <p className="text-sm" style={{ color: cardMuted }}>No power data yet.</p>
          )}
        </AccordionSection>

        <AccordionSection
          title={
            <span className="flex items-center gap-2 min-w-0 w-full">
              <span className="shrink-0">Backup Power for:</span>
              <select
                value={focusInverterValid}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onChange={(e) => {
                  const id = e.target.value;
                  setFocusInverterId(id);
                  if (id) highlightCanvasEquipment(id);
                }}
                className="flex-1 min-w-0 text-sm font-medium rounded px-2 py-1 outline-none"
                style={{ backgroundColor: isDark ? '#2b3140' : '#1e293b', color: cardText, border: `1px solid ${border}` }}
                title="Show backup power for one inverter or the whole design"
              >
                <option value="">Whole design</option>
                {backupIslands.map((i) => (
                  <option key={i.inverterId} value={i.inverterId}>
                    {i.inverterName}
                  </option>
                ))}
              </select>
            </span>
          }
          expanded={openSections.backupPower}
          onToggle={() => toggleSection('backupPower')}
          muted={cardMuted}
          cardBg={cardBg}
          cardText={cardText}
        >
          {!backupView ? (
            <p className="text-sm" style={{ color: cardMuted }}>Nothing added yet.</p>
          ) : (
            <BackupIslandView
              island={backupView}
              isDark={isDark}
              cardMuted={cardMuted}
              labelTrackers={!focusInverterValid && backupIslands.length > 1}
            />
          )}
        </AccordionSection>

        <AccordionSection
          title="Device Issues"
          badge={
            !ok ? (
              <span className="text-xs font-medium text-amber-400">
                {errors.length}/{warnings.length}
              </span>
            ) : (
              <CheckCircle2 size={16} className="text-emerald-400" />
            )
          }
          expanded={openSections.issues}
          onToggle={() => toggleSection('issues')}
          muted={cardMuted}
          cardBg={cardBg}
          cardText={cardText}
        >
          <div className="space-y-3">
            <div className="flex flex-wrap gap-1.5">
              {FILTERS.map((f) => {
                const selected = issueFilter === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setIssueFilter(f.id)}
                    aria-pressed={selected}
                    className="px-2.5 py-1 rounded text-sm font-medium"
                    style={{
                      backgroundColor: selected ? '#2563eb' : 'rgba(255,255,255,0.1)',
                      color: selected ? '#ffffff' : cardText,
                    }}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 rounded px-2 py-1.5" style={{ backgroundColor: isDark ? '#171a1f' : '#1e293b', color: cardText }}>
              <Search size={16} className="text-gray-400 shrink-0" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search issues"
                className="flex-1 bg-transparent text-sm outline-none"
              />
            </div>
            <div className="space-y-2 text-sm">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={hideIdle} onChange={(e) => setHideIdle(e.target.checked)} />
                Hide idle devices
              </label>
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                <span style={{ color: cardMuted }}>Group by</span>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="logic-issue-group"
                    checked={issueGroupBy === 'device'}
                    onChange={() => setIssueGroupBy('device')}
                  />
                  Device
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="logic-issue-group"
                    checked={issueGroupBy === 'issue'}
                    onChange={() => setIssueGroupBy('issue')}
                  />
                  Issue
                </label>
              </div>
            </div>
            {ok && errors.length === 0 && designIssues.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6 text-emerald-400">
                <CheckCircle2 size={36} />
                <div className="text-base font-medium mt-2">All good</div>
              </div>
            ) : (
              <div className="space-y-2">
                {grouped.map(([key, list]) => (
                  <IssueGroup
                    key={key}
                    title={issueGroupBy === 'device' ? (result?.devices[key]?.name || key) : key}
                    issues={list}
                  />
                ))}
              </div>
            )}
          </div>
        </AccordionSection>

        <AccordionSection
          title="Devices"
          badge={
            <span className="text-xs font-medium" style={{ color: cardMuted }}>
              {includedCount}/{allDevices.length}
            </span>
          }
          expanded={openSections.devices}
          onToggle={() => toggleSection('devices')}
          muted={cardMuted}
          cardBg={cardBg}
          cardText={cardText}
        >
          <DeviceList
            devices={allDevices}
            focusId={focusDeviceId}
            muted={cardMuted}
            onToggle={(id, nextIncluded) => setNodesLogicExcluded([id], !nextIncluded)}
            onSelect={selectDevice}
          />
        </AccordionSection>
      </div>
    </div>
  );
};

function IssueGroup({ title, issues }: { title: string; issues: LogicIssue[] }) {
  const [open, setOpen] = React.useState(true);
  const err = issues.filter((i) => i.severity === 'error').length;
  const groupEquipmentId = issues.find((i) => i.equipmentId)?.equipmentId;
  return (
    <div
      className="rounded-md bg-white/5 overflow-hidden"
      onMouseEnter={() => highlightCanvasEquipment(groupEquipmentId)}
      onMouseLeave={() => highlightCanvasEquipment(null)}
    >
      <button type="button" className="w-full flex items-center gap-1.5 px-2.5 py-2 text-sm font-medium" onClick={() => setOpen((v) => !v)}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="truncate flex-1 text-left">{title}</span>
        {err > 0 && <span className="text-red-400">{err}</span>}
      </button>
      {open && issues.map((i) => <IssueRow key={i.id} issue={i} />)}
    </div>
  );
}

function IssueRow({ issue }: { issue: LogicIssue }) {
  const color =
    issue.severity === 'error' ? 'text-red-400' : issue.severity === 'warning' ? 'text-amber-400' : 'text-sky-400';
  const Icon = issue.severity === 'error' ? AlertTriangle : issue.category === 'power' ? ZapOff : Cpu;
  return (
    <button
      type="button"
      onClick={() => focusIssue(issue)}
      onMouseEnter={() => highlightCanvasEquipment(issue.equipmentId)}
      className="w-full text-left px-2.5 py-2 hover:bg-white/10 rounded"
    >
      <div className={`flex items-start gap-2 text-sm ${color}`}>
        <Icon size={15} className="mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="font-medium leading-snug">{issue.message}</div>
          {issue.details && <div className="text-xs text-slate-300 mt-1 leading-snug">{issue.details}</div>}
        </div>
      </div>
    </button>
  );
}

export default LogicStatusDrawer;
